import { transformHyphens } from "@bmi-digital/components/utils";
import React from "react";
import { Helmet } from "react-helmet";
import { useConfig } from "../contexts/ConfigProvider";
import { Product } from "../types/pim";
import { getJpgImage } from "../utils/media";
import { getPathWithCountryCode } from "../utils/path";
import { createSchemaOrgForHomeAndBrandPage } from "../utils/schemaOrgHomeAndBrandPage";
import { createSchemaOrgDataForPdpPage } from "../utils/schemaOrgPDPpage";
import { SEOContentData as SEOContentData } from "./SEOContent";

interface HeadProps {
  htmlAttributes: { lang: string };
  title: string;
  defer?: boolean;
  ogImageUrl?: string | null;
  seo: SEOContentData | null;
  path: string;
  variantProduct?: Product;
  countryCode: string;
  pageType?: string;
  brandLogo?: string | null;
}

export const Head = ({
  htmlAttributes,
  title,
  defer = false,
  ogImageUrl,
  seo,
  path,
  variantProduct,
  countryCode,
  pageType,
  brandLogo
}: HeadProps) => {
  const {
    excludeLocalisedAlternate,
    isPreviewMode,
    hubSpotId,
    isSchemaORGActivated,
    oneTrustId,
    vwoAccountId
  } = useConfig();
  const imageUrl = getJpgImage(ogImageUrl);
  const finalTitle = (seo?.title || transformHyphens(title))?.trim();

  const isScriptOnetrustEnabled = Boolean(oneTrustId);
  const enableHubSpot = Boolean(!isPreviewMode && hubSpotId);

  const schemaOrgActivated =
    Boolean(isSchemaORGActivated) && Boolean(variantProduct);

  // 1. Calculate the raw description, checking all sources
  const rawSeoDescription =
    (variantProduct &&
      (variantProduct.seoDescription || variantProduct.description)) ||
    (seo && seo.description);

  // 2. 🎯 Calculate the final, TRIMMED description
  const finalSeoDescription = rawSeoDescription
    ? rawSeoDescription.trim()
    : undefined;

  const correctGatsbySiteUrl: string = process.env.GATSBY_SITE_URL!.endsWith(
    "/"
  )
    ? process.env.GATSBY_SITE_URL!.slice(0, -1)
    : process.env.GATSBY_SITE_URL!;
  return (
    <Helmet htmlAttributes={htmlAttributes} title={finalTitle} defer={defer}>
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      <meta
        property="og:url"
        content={`${correctGatsbySiteUrl}${getPathWithCountryCode(
          countryCode,
          path
        )}`}
      />
      <meta property="og:title" content={finalTitle} />
      {seo?.noIndex && <meta name="robots" content="noindex, nofollow" />}
      {/* NOTE: expand viewport beyond safe area */}
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1.0, viewport-fit=cover"
      />
      {finalSeoDescription && (
        <meta
          name="description"
          property="og:description"
          content={finalSeoDescription}
        />
      )}
      {variantProduct?.seoTags && (
        <meta name="keywords" content={variantProduct.seoTags.join(",")} />
      )}
      <script>{`
        function detectIE() {
          if (typeof window !== "undefined" && window.location.pathname === "${getPathWithCountryCode(
            countryCode,
            "ie-dialog"
          )}") {
            return false;
          }
          var userAgent = typeof window !== "undefined" ? window.navigator.userAgent : "";
          var documentMode = typeof window !== "undefined" ? window.document.documentMode : 0;
          return userAgent.indexOf("MSIE") > 0 || userAgent.indexOf("Trident") > 0 || documentMode;
        };

        if (detectIE()) {
          window.location = "${getPathWithCountryCode(
            countryCode,
            "ie-dialog"
          )}"
        }
      `}</script>
      {!excludeLocalisedAlternate && htmlAttributes.lang && (
        <link
          rel="alternate"
          href={`${correctGatsbySiteUrl}${getPathWithCountryCode(
            countryCode,
            path
          )}`}
          hrefLang={htmlAttributes.lang}
        />
      )}
      <link
        rel="alternate"
        href={`${correctGatsbySiteUrl}${getPathWithCountryCode(
          countryCode,
          path
        )}`}
        hrefLang="x-default"
      />
      {isScriptOnetrustEnabled && (
        <script
          type="text/javascript"
          src={`https://cdn.cookielaw.org/consent/${oneTrustId}/OtAutoBlock.js`}
        />
      )}
      {isScriptOnetrustEnabled && (
        <script
          src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
          type="text/javascript"
          data-domain-script={oneTrustId}
        />
      )}
      {isScriptOnetrustEnabled && (
        <script type="text/javascript">{`
              function OptanonWrapper() { 
                  console.log("OptanonWrapper called");
              }
        `}</script>
      )}
      {enableHubSpot && (
        // This script is for the HubSpot CTA Links (see `Link.tsx`)
        <script
          id="hubspot-cta-script"
          src="https://js.hscta.net/cta/current.js"
          async
        />
      )}
      {vwoAccountId && (
        <link rel="preconnect" href="https://dev.visualwebsiteoptimizer.com" />
      )}
      {/** Can not use only one condition to return both VWO link and script
       wrapped into a single React fragment because Helmet throws an error
       if we use a React fragment inside it
       */}
      {vwoAccountId && (
        <script type="text/javascript" id="vwoCode">
          {`
              window._vwo_code || (function() {
              var account_id=${vwoAccountId},
              version=2.1,
              settings_tolerance=2000,
              hide_element='body',
              hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important;transition:none !important;',
              /* DO NOT EDIT BELOW THIS LINE */
              f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={nonce:v&&v.nonce,use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},finished:function(){return f},addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}v&&t.setAttribute('nonce',v.nonce);d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var n=this.getSettings(),i=d.createElement('script'),r=this;t=t||{};if(n){i.textContent=n;d.getElementsByTagName('head')[0].appendChild(i);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200||o.status===304){_vwo_code.addScript({text:o.responseText})}else{_vwo_code.finish('&e=loading_failure:'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}_vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t=d.createElement('style');var n=this.hide_element(),i=n?n+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=i;else t.appendChild(d.createTextNode(i));r.appendChild(t)}else{t=d.getElementsByTagName('head')[0];var i=d.createElement('div');i.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;';i.setAttribute('id','_vis_opt_path_hides');i.classList.add('_vis_hide_layer');t.parentNode.insertBefore(i,t.nextSibling)}var o=window._vis_opt_url||d.URL,s='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(o)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({src:s})}else{this.load(s+'&x=true')}}};w._vwo_code=code;code.init();})();
          `}
        </script>
      )}
      <script lang="javascript">
        {`
            if(history && history.scrollRestoration && history.scrollRestoration !== 'manual') {
              history.scrollRestoration = 'manual';
            }
          `}
      </script>
      {schemaOrgActivated && (
        <script type="application/ld+json">
          {JSON.stringify(
            createSchemaOrgDataForPdpPage(
              correctGatsbySiteUrl,
              variantProduct,
              countryCode
            )
          )}
        </script>
      )}
      {!!pageType && (
        <script type="application/ld+json">
          {JSON.stringify(
            createSchemaOrgForHomeAndBrandPage(
              correctGatsbySiteUrl,
              pageType,
              htmlAttributes.lang,
              path,
              title,
              countryCode,
              finalSeoDescription,
              brandLogo || undefined,
              seo?.sameAs || undefined
            )
          )}
        </script>
      )}
    </Helmet>
  );
};
